import { useParams } from "@tanstack/react-router";

/**
 * Build a locale (+ optional schoolyear) prefixed path, e.g.
 * buildLocaleHref("/api/acme/resource", { locale: "nl" }) => "/nl/api/acme/resource"
 *
 * NON-ROUTER URLs ONLY: api endpoints (form actions, fetch bases, resource/upload URLs), the
 * deliberate `window.location.href` hard reloads, and session-stored redirect strings. Router
 * navigation must use a typed `to` + `params` instead — never interpolate a path into a string
 * (see the epic that removed LocaleLink/useLocaleHref/hrefRedirect).
 */
export function buildLocaleHref(
  path: string,
  params: { locale: string; schoolyear?: string },
): string {
  const schoolyear = params.schoolyear ? `/${params.schoolyear}` : "";
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `/${params.locale}${schoolyear}${suffix}`;
}

/**
 * Link params for shared components, which have no `Route.fullPath` to narrow `params={(prev) => …}`
 * against. Route files should use `from={Route.fullPath}` instead of these.
 */
export function useLocaleParams() {
  const params = useParams({ strict: false });
  return { locale: params.locale ?? "nl", schoolyear: params.schoolyear };
}

/**
 * As `useLocaleParams`, plus the current `school`. `school` is undefined outside the `/$school`
 * scope (admin, auth pages), where shared chrome like the header/footer/user-menu still renders —
 * so guard on it rather than assuming it. `useSchoolParamsStrict` is for components that genuinely
 * cannot render school-less.
 */
export function useSchoolParams() {
  const params = useParams({ strict: false });
  return {
    locale: params.locale ?? "nl",
    schoolyear: params.schoolyear,
    school: params.school,
  };
}

/** `useSchoolParams` for components that only ever render inside the `/$school` scope. */
export function useSchoolParamsStrict() {
  const params = useSchoolParams();
  if (!params.school) {
    throw new Error("useSchoolParamsStrict() used outside a /$school route");
  }
  return { ...params, school: params.school };
}
